import { STATE_CODES } from 'constants/states';
import { t } from 'i18next';
import { validateDateOfBirth } from 'utils/validate-date-of-birth';
import * as Yup from 'yup';

// Source: https://emailregex.com/
const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const epionDemographicDataSchema = Yup.object()
    .required()
    .shape({
        firstName: Yup.string().required(t('form.validation.firstName.required')),
        lastName: Yup.string().required(t('form.validation.lastName.required')),
        dateOfBirth: Yup.string()
            .required(t('form.validation.dateOfBirth.required'))
            .test('valid', t('form.validation.dateOfBirth.required'), validateDateOfBirth),
        sex: Yup.mixed<'FEMALE' | 'MALE'>().oneOf(['FEMALE', 'MALE']).required(t('form.validation.sex.required')),
        email: Yup.string().when('noEmail', {
            is: (noEmail: boolean) => noEmail,
            then: (schema: Yup.StringSchema) => schema.notRequired().matches(emailRegex, t('form.validation.email.invalid')),
            otherwise: (schema: Yup.StringSchema) =>
                schema.required(t('form.validation.email.required')).matches(emailRegex, t('form.validation.email.invalid')),
        }),
        noEmail: Yup.boolean().required(),
        phoneNumber: Yup.string()
            .required(t('form.validation.phone.required'))
            .transform((value: string | number | undefined) => {
                return typeof value === 'string' ? value.replaceAll(/\D/g, '') : value;
            })
            // https://en.wikipedia.org/wiki/North_American_Numbering_Plan#Modern_plan
            .matches(/^([2-9][0-9]{2})([2-9][0-9]{2})(\d{4})$/, t('form.validation.phone.invalid')),
        phoneType: Yup.mixed<'HOME' | 'MOBILE'>().oneOf(['HOME', 'MOBILE']).required(t('form.validation.phoneType.required')),
        address: Yup.string().required(t('form.validation.address.required')),
        addressCount: Yup.string(),
        city: Yup.string().required(t('form.validation.city.required')),
        state: Yup.mixed<(typeof STATE_CODES)[number]>()
            .oneOf(STATE_CODES, t('form.validation.state.invalid'))
            .required(t('form.validation.state.required')),
        zip: Yup.string().ensure().matches(/\d{5}/, t('form.validation.zip.invalid')).required(t('form.validation.zip.required')),
    });

export interface EpionDemographicDataSchemaValidated {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    sex: 'FEMALE' | 'MALE';
    email?: string;
    noEmail: boolean;
    phoneNumber: string;
    phoneType: 'HOME' | 'MOBILE';
    address: string;
    addressCount?: string;
    city: string;
    state: (typeof STATE_CODES)[number];
    zip: string;
}

export type EpionPatientInformationFormSchema = Partial<EpionDemographicDataSchemaValidated>;
